import { Button, Empty, Input, Spin } from 'antd'
import { useDatapointSelector } from 'features/systemLens/useDatapointSelector'
import { Search, X } from 'lucide-react'
import React, { useMemo } from 'react'
import { FixedSizeList as List } from 'react-window'
import styled from 'styled-components'
import { BaseSensorEntry } from 'types'

import { AdvancedFilterModal } from './AdvancedFilterModal'
import { Row } from './DatapointSelectorRow'

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  width: 100%;
`

const SearchContainer = styled.div`
  display: flex;
  gap: 0.5rem;
  align-items: center;
`

const SelectedTagsContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin-top: 0.5rem;
`

const TagUnit = styled.span`
  opacity: 0.7;
`

const RemoveButton = styled.span`
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: ${({ theme }) => theme.forecast.text2};
  transition: color 0.2s;

  &:hover {
    color: ${({ theme }) => theme.forecast.danger};
  }
`

const TagChip = styled.div`
  display: flex;
  align-items: center;
  gap: 0.25rem;
  padding: 0.25rem 0.5rem;
  background: ${({ theme }) => theme.forecast.background3};
  border: 0.0625rem solid ${({ theme }) => theme.forecast.border};
  border-radius: 0.25rem;
  font-size: 0.75rem;
  color: ${({ theme }) => theme.forecast.text1};
`

const QuickResultsList = styled.div`
  background: ${({ theme }) => theme.forecast.background2};
  border: 0.0625rem solid ${({ theme }) => theme.forecast.border};
  border-radius: 0.25rem;
  margin-top: 0.5rem;
  overflow: hidden;
  height: 300px;
`

const EmptyContainer = styled.div`
  padding: 1rem;
  text-align: center;
`

const SearchWrapper = styled.div`
  position: relative;
  flex: 1;
`

const SearchInputContainer = styled.div`
  display: flex;
  gap: 0.5rem;
  align-items: center;
`

interface DatapointSelectorProps {
  tagsOptions: BaseSensorEntry[]
  selectedTags: BaseSensorEntry[]
  setSelectedTags: React.Dispatch<React.SetStateAction<BaseSensorEntry[]>>
  isLoadingSensors: boolean
  onToggleTags?: (tags: BaseSensorEntry[]) => void
  onRemoveTags?: (tags: BaseSensorEntry[]) => void
  placeholder?: string
}

export const DatapointSelector: React.FC<DatapointSelectorProps> = ({
  tagsOptions,
  placeholder = 'Search name, ID, or description...',
  selectedTags,
  setSelectedTags,
  isLoadingSensors,
  onToggleTags,
  onRemoveTags,
}) => {
  const {
    searchQuery,
    setSearchQuery,
    showAdvancedFilter,
    setShowAdvancedFilter,
    handleRemoveTag,
    handleAdvancedFilterApply,
    advancedFilters,
    quickSearchResults,
    handleTagToggle,
  } = useDatapointSelector(
    tagsOptions,
    selectedTags,
    setSelectedTags,
    onToggleTags,
    onRemoveTags
  )

  // Memoize itemData to prevent react-window from resetting scroll position
  const itemData = useMemo(
    () => ({
      tags: quickSearchResults,
      selectedTags,
      handleTagToggle,
    }),
    [quickSearchResults, selectedTags, handleTagToggle]
  )
  return (
    <Container>
      <SearchContainer>
        <SearchWrapper>
          <SearchInputContainer>
            <Input
              prefix={<Search size={16} />}
              placeholder={placeholder}
              value={searchQuery}
              onChange={(e) => {
                setSearchQuery(e.target.value)
              }}
              allowClear
            />

            <Button onClick={() => setShowAdvancedFilter(true)}>
              Advanced Search
            </Button>
          </SearchInputContainer>
          <QuickResultsList>
            {isLoadingSensors ? (
              <EmptyContainer>
                <Spin size='small' />
              </EmptyContainer>
            ) : quickSearchResults.length === 0 ? (
              <EmptyContainer>
                <Empty
                  description='No tags found'
                  image={Empty.PRESENTED_IMAGE_SIMPLE}
                />
              </EmptyContainer>
            ) : (
              <List
                height={280}
                itemCount={quickSearchResults.length}
                itemSize={45}
                width='100%'
                overscanCount={20}
                itemData={itemData}>
                {Row}
              </List>
            )}
          </QuickResultsList>
        </SearchWrapper>
      </SearchContainer>

      {selectedTags.length > 0 && (
        <SelectedTagsContainer>
          {selectedTags.map((tag) => (
            <TagChip key={tag.id}>
              <span>{tag.data.name}</span>
              {tag.data.unit && <TagUnit>({tag.data.unit})</TagUnit>}
              <RemoveButton onClick={() => handleRemoveTag(tag.id)}>
                <X size={14} />
              </RemoveButton>
            </TagChip>
          ))}
        </SelectedTagsContainer>
      )}

      <AdvancedFilterModal
        open={showAdvancedFilter}
        onClose={() => setShowAdvancedFilter(false)}
        tags={tagsOptions}
        selectedTags={selectedTags}
        currentFilters={advancedFilters}
        onApply={handleAdvancedFilterApply}
      />
    </Container>
  )
}
